import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import CopyToClipboardButton from "components/shared/CopyToClipboardButton";
import MessageBlock from "components/shared/MessageBlock";
import StyledButton from "components/shared/StyledButton";
import StyledCheckbox from "components/shared/StyledCheckbox";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { downloadFile } from "helpers/utils";
import { useTranslation } from "react-i18next";
import type { APIKey } from "store/settingsStore";

export type CreatedAPIKey = APIKey & { key: string };

type CreatedKeyStepProps = {
    createdKey: CreatedAPIKey;
    keyAcknowledged: boolean;
    onAcknowledge: (checked: boolean) => void;
    onClose: () => void;
};

const apiKeyFileBaseName = (name: string) => {
    const slug = name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 40);
    return slug ? `crealo-api-key-${slug}` : "crealo-api-key";
};

const downloadGeneratedKey = (created: CreatedAPIKey, format: "txt" | "json") => {
    const baseName = apiKeyFileBaseName(created.name);
    const contents =
        format === "txt"
            ? `${created.key}\n`
            : `${JSON.stringify(
                  {
                      name: created.name,
                      key: created.key,
                      role: created.keyScope,
                      expiresAt: created.expiryDate ?? null,
                  },
                  null,
                  2,
              )}\n`;
    downloadFile({
        source: new Blob([contents], {
            type: format === "txt" ? "text/plain;charset=utf-8" : "application/json",
        }),
        fileName: `${baseName}.${format}`,
    });
};

const CreatedKeyStep = ({ createdKey, keyAcknowledged, onAcknowledge, onClose }: CreatedKeyStepProps) => {
    const [t] = useTranslation();

    return (
        <div className="create-key-wrapper">
            <div className="create-key-description">
                <MessageBlock type="warning" text={t("Settings.APIKey.Modals.CreateKey.OnceGenerated")} small />
            </div>
            <div className="create-key-input">
                <label className="create-key-input-label">{t("Settings.APIKey.Key")}</label>
                <StyledTextField
                    fullWidth
                    value={createdKey.key}
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <CopyToClipboardButton color="" value={createdKey.key} style={{ height: 16, width: 16 }} />
                        ),
                    }}
                />
            </div>
            <div className="create-key-downloads">
                <button
                    type="button"
                    className="create-key-download"
                    onClick={() => downloadGeneratedKey(createdKey, "txt")}
                >
                    <span className="create-key-download-type">.txt</span>
                    <span className="create-key-download-text">
                        {t("Settings.APIKey.Modals.CreateKey.DownloadTxtHint")}
                    </span>
                    <FileDownloadOutlined />
                </button>
                <button
                    type="button"
                    className="create-key-download"
                    onClick={() => downloadGeneratedKey(createdKey, "json")}
                >
                    <span className="create-key-download-type">.json</span>
                    <span className="create-key-download-text">
                        {t("Settings.APIKey.Modals.CreateKey.DownloadJsonHint")}
                    </span>
                    <FileDownloadOutlined />
                </button>
            </div>
            <label className="create-key-acknowledge">
                <StyledCheckbox checked={keyAcknowledged} onChange={(event) => onAcknowledge(event.target.checked)} />
                <span>{t("Settings.APIKey.Modals.CreateKey.Acknowledge")}</span>
            </label>
            <StyledButton buttonType="primary" fullWidth disabled={!keyAcknowledged} onClick={onClose}>
                {t("Settings.APIKey.Modals.CreateKey.Close")}
            </StyledButton>
        </div>
    );
};

export default CreatedKeyStep;
